import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import EnglishDatePicker from '../components/EnglishDatePicker/EnglishDatePicker';
import './AdminDeleteDashboard.css';

const AdminDeleteDashboard = () => {
  const { user, token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('pours');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [logs, setLogs] = useState([]);

  // ===== بيانات الصبات =====
  const [pours, setPours] = useState([]);
  const [loadingPours, setLoadingPours] = useState(false);
  const [deleteMode, setDeleteMode] = useState('single');
  const [selectedPourId, setSelectedPourId] = useState('');
  const [selectedPourIds, setSelectedPourIds] = useState([]);
  const [station, setStation] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stations, setStations] = useState([]);

  // ===== بيانات التكسير =====
  const [breakingMode, setBreakingMode] = useState('single');
  const [selectedBreakingPourId, setSelectedBreakingPourId] = useState('');
  const [breakingResults, setBreakingResults] = useState([]);
  const [selectedResultId, setSelectedResultId] = useState('');
  const [loadingBreakingResults, setLoadingBreakingResults] = useState(false);

  // ===== بيانات الحرارة =====
  const [tempMode, setTempMode] = useState('single');
  const [selectedTempPourId, setSelectedTempPourId] = useState('');
  const [tempReadings, setTempReadings] = useState([]);
  const [selectedReadingId, setSelectedReadingId] = useState('');
  const [loadingTempReadings, setLoadingTempReadings] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

  // ===== جلب الصبات =====
  useEffect(() => {
    fetchPours();
  }, []);

  const fetchPours = async () => {
    try {
      setLoadingPours(true);
      const res = await axios.get(
        `${API_URL}/pours?status=all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPours(res.data.data || []);
      
      // استخراج المحطات الفريدة
      const uniqueStations = [...new Set(res.data.data?.map(p => p.station))];
      setStations(uniqueStations);
    } catch (error) {
      console.error('Error fetching pours:', error);
      setMessageType('error');
      setMessage('❌ فشل في جلب الصبات');
    } finally {
      setLoadingPours(false);
    }
  };

  // ===== جلب نتائج التكسير لصبة =====
  const fetchBreakingResults = async (pourId) => {
    try {
      setLoadingBreakingResults(true);
      const res = await axios.get(
        `${API_URL}/pours/${pourId}/breaking-results`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBreakingResults(res.data.data || []);
    } catch (error) {
      console.error('Error fetching breaking results:', error);
      setBreakingResults([]);
    } finally {
      setLoadingBreakingResults(false);
    }
  };

  // ===== جلب قراءات الحرارة لصبة =====
  const fetchTempReadings = async (pourId) => {
    try {
      setLoadingTempReadings(true);
      const res = await axios.get(
        `${API_URL}/pours/${pourId}/temperature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTempReadings(res.data.data || []);
    } catch (error) {
      console.error('Error fetching temperature readings:', error);
      setTempReadings([]);
    } finally {
      setLoadingTempReadings(false);
    }
  };

  // ===== إضافة log =====
  const addLog = (action, status, details) => {
    const newLog = {
      id: Date.now(),
      timestamp: new Date().toLocaleString('ar-EG'),
      action,
      status,
      details
    };
    setLogs([newLog, ...logs]);
  };

  // ===== حذف الصبات =====
  const handleDeletePour = async () => {
    try {
      setLoading(true);
      setMessage('');

      if (deleteMode === 'single') {
        if (!selectedPourId) {
          setMessageType('error');
          setMessage('❌ اختر صبة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/${selectedPourId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ ${res.data.message}`);
        addLog('حذف صبة واحدة', 'نجح', `ID: ${selectedPourId}`);
        setSelectedPourId('');
        fetchPours();
      } else if (deleteMode === 'multiple') {
        if (selectedPourIds.length === 0) {
          setMessageType('error');
          setMessage('❌ اختر صبات');
          return;
        }
        const res = await axios.post(
          `${API_URL}/pours/delete-multiple`,
          { pourIds: selectedPourIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ تم حذف ${res.data.deletedCount} صبة`);
        addLog('حذف صبات متعددة', 'نجح', `${res.data.deletedCount} صبة`);
        setSelectedPourIds([]);
        fetchPours();
      } else if (deleteMode === 'station') {
        if (!station) {
          setMessageType('error');
          setMessage('❌ اختر محطة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/station/${station}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ تم حذف ${res.data.deletedCount} صبة من المحطة`);
        addLog('حذف صبات المحطة', 'نجح', `${station}: ${res.data.deletedCount}`);
        setStation('');
        fetchPours();
      } else if (deleteMode === 'dateRange') {
        if (!dateFrom || !dateTo) {
          setMessageType('error');
          setMessage('❌ أدخل التاريخ من والى');
          return;
        }
        const res = await axios.post(
          `${API_URL}/pours/delete-by-date-range`,
          { dateFrom, dateTo },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ تم حذف ${res.data.deletedCount} صبة`);
        addLog('حذف فترة زمنية', 'نجح', `${dateFrom} إلى ${dateTo}`);
        setDateFrom('');
        setDateTo('');
        fetchPours();
      }
    } catch (error) {
      setMessageType('error');
      setMessage(`❌ ${error.response?.data?.message || 'حدث خطأ'}`);
      addLog('حذف صبة', 'فشل', error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== حذف نتائج التكسير =====
  const handleDeleteBreakingResult = async () => {
    try {
      setLoading(true);
      setMessage('');

      if (breakingMode === 'single') {
        if (!selectedBreakingPourId || !selectedResultId) {
          setMessageType('error');
          setMessage('❌ اختر صبة ونتيجة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/${selectedBreakingPourId}/breaking-results/${selectedResultId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ ${res.data.message}`);
        addLog('حذف نتيجة تكسير', 'نجح', selectedBreakingPourId);
        setSelectedResultId('');
        fetchBreakingResults(selectedBreakingPourId);
      } else if (breakingMode === 'all') {
        if (!selectedBreakingPourId) {
          setMessageType('error');
          setMessage('❌ اختر صبة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/${selectedBreakingPourId}/breaking-results-all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ تم حذف ${res.data.deletedCount} نتيجة`);
        addLog('حذف كل النتائج', 'نجح', `${res.data.deletedCount} نتيجة`);
        setBreakingResults([]);
      }
    } catch (error) {
      setMessageType('error');
      setMessage(`❌ ${error.response?.data?.message || 'حدث خطأ'}`);
      addLog('حذف نتيجة التكسير', 'فشل', error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== حذف قراءات الحرارة =====
  const handleDeleteTempReading = async () => {
    try {
      setLoading(true);
      setMessage('');

      if (tempMode === 'single') {
        if (!selectedTempPourId || !selectedReadingId) {
          setMessageType('error');
          setMessage('❌ اختر صبة وقراءة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/${selectedTempPourId}/temperature/${selectedReadingId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ ${res.data.message}`);
        addLog('حذف قراءة حرارة', 'نجح', selectedTempPourId);
        setSelectedReadingId('');
        fetchTempReadings(selectedTempPourId);
      } else if (tempMode === 'all') {
        if (!selectedTempPourId) {
          setMessageType('error');
          setMessage('❌ اختر صبة');
          return;
        }
        const res = await axios.delete(
          `${API_URL}/pours/${selectedTempPourId}/temperature-all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessageType('success');
        setMessage(`✅ تم حذف ${res.data.deletedCount} قراءة`);
        addLog('حذف كل القراءات', 'نجح', `${res.data.deletedCount} قراءة`);
        setTempReadings([]);
      }
    } catch (error) {
      setMessageType('error');
      setMessage(`❌ ${error.response?.data?.message || 'حدث خطأ'}`);
      addLog('حذف قراءة الحرارة', 'فشل', error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="admin-delete-dashboard">
      <div className="delete-header">
        <h1>🗑️ لوحة حذف البيانات</h1>
        <p>تحكم كامل للـ Admin في حذف الصبات والمكعبات وقراءات الحرارة</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'pours' ? 'active' : ''}`}
            onClick={() => setActiveTab('pours')}
          >
            📋 حذف الصبات
          </button>
          <button
            className={`tab-btn ${activeTab === 'breaking' ? 'active' : ''}`}
            onClick={() => setActiveTab('breaking')}
          >
            💪 حذف التكسير
          </button>
          <button
            className={`tab-btn ${activeTab === 'temperature' ? 'active' : ''}`}
            onClick={() => setActiveTab('temperature')}
          >
            🌡️ حذف الحرارة
          </button>
          <button
            className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📊 السجلات
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      {/* ===== تاب حذف الصبات ===== */}
      {activeTab === 'pours' && (
        <div className="tab-content">
          <div className="delete-modes">
            <label className="mode-radio">
              <input
                type="radio"
                value="single"
                checked={deleteMode === 'single'}
                onChange={(e) => setDeleteMode(e.target.value)}
              />
              <span>حذف صبة واحدة</span>
            </label>
            <label className="mode-radio">
              <input
                type="radio"
                value="multiple"
                checked={deleteMode === 'multiple'}
                onChange={(e) => setDeleteMode(e.target.value)}
              />
              <span>حذف صبات متعددة</span>
            </label>
            <label className="mode-radio">
              <input
                type="radio"
                value="station"
                checked={deleteMode === 'station'}
                onChange={(e) => setDeleteMode(e.target.value)}
              />
              <span>حذف صبات محطة</span>
            </label>
            <label className="mode-radio">
              <input
                type="radio"
                value="dateRange"
                checked={deleteMode === 'dateRange'}
                onChange={(e) => setDeleteMode(e.target.value)}
              />
              <span>حذف فترة زمنية</span>
            </label>
          </div>

          <div className="form-group">
            {deleteMode === 'single' && (
              <select
                value={selectedPourId}
                onChange={(e) => setSelectedPourId(e.target.value)}
                disabled={loadingPours}
              >
                <option value="">اختر صبة ({pours.length})</option>
                {pours.map((pour) => (
                  <option key={pour._id} value={pour._id}>
                    {pour.station} - {new Date(pour.castingDate).toLocaleDateString('ar-EG')} - {pour.location}
                  </option>
                ))}
              </select>
            )}
            {deleteMode === 'multiple' && (
              <div className="checkbox-list">
                <p>اختر الصبات المراد حذفها ({selectedPourIds.length})</p>
                <div className="checkboxes">
                  {pours.map((pour) => (
                    <label key={pour._id} className="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPourIds.includes(pour._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPourIds([...selectedPourIds, pour._id]);
                          } else {
                            setSelectedPourIds(selectedPourIds.filter(id => id !== pour._id));
                          }
                        }}
                      />
                      <span>{pour.station} - {new Date(pour.castingDate).toLocaleDateString('ar-EG')}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {deleteMode === 'station' && (
              <select
                value={station}
                onChange={(e) => setStation(e.target.value)}
              >
                <option value="">اختر محطة</option>
                {stations.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            )}
            {deleteMode === 'dateRange' && (
              <div className="date-range">
                <EnglishDatePicker
                  selected={dateFrom ? new Date(dateFrom) : null}
                  onChange={(date) => setDateFrom(date ? date.toISOString().split('T')[0] : '')}
                  placeholderText="من تاريخ"
                />
                <EnglishDatePicker
                  selected={dateTo ? new Date(dateTo) : null}
                  onChange={(date) => setDateTo(date ? date.toISOString().split('T')[0] : '')}
                  placeholderText="إلى تاريخ"
                />
              </div>
            )}
          </div>

          <button
            className="delete-btn"
            onClick={handleDeletePour}
            disabled={loading}
          >
            {loading ? '⏳ جاري الحذف...' : '🗑️ حذف الآن'}
          </button>
        </div>
      )}

      {/* ===== تاب حذف التكسير ===== */}
      {activeTab === 'breaking' && (
        <div className="tab-content">
          <div className="delete-modes">
            <label className="mode-radio">
              <input
                type="radio"
                value="single"
                checked={breakingMode === 'single'}
                onChange={(e) => setBreakingMode(e.target.value)}
              />
              <span>حذف نتيجة واحدة</span>
            </label>
            <label className="mode-radio">
              <input
                type="radio"
                value="all"
                checked={breakingMode === 'all'}
                onChange={(e) => setBreakingMode(e.target.value)}
              />
              <span>حذف كل النتائج</span>
            </label>
          </div>

          <div className="form-group">
            <select
              value={selectedBreakingPourId}
              onChange={(e) => {
                setSelectedBreakingPourId(e.target.value);
                if (e.target.value) {
                  fetchBreakingResults(e.target.value);
                }
              }}
              disabled={loadingPours}
            >
              <option value="">اختر صبة ({pours.length})</option>
              {pours.map((pour) => (
                <option key={pour._id} value={pour._id}>
                  {pour.station} - {new Date(pour.castingDate).toLocaleDateString('ar-EG')}
                </option>
              ))}
            </select>

            {breakingMode === 'single' && (
              <select
                value={selectedResultId}
                onChange={(e) => setSelectedResultId(e.target.value)}
                disabled={loadingBreakingResults || breakingResults.length === 0}
              >
                <option value="">اختر نتيجة ({breakingResults.length})</option>
                {breakingResults.map((result) => (
                  <option key={result._id} value={result._id}>
                    Day {result.age} - {new Date(result.testDate).toLocaleDateString('ar-EG')}
                  </option>
                ))}
              </select>
            )}
          </div>

          <button
            className="delete-btn"
            onClick={handleDeleteBreakingResult}
            disabled={loading}
          >
            {loading ? '⏳ جاري الحذف...' : '🗑️ حذف الآن'}
          </button>
        </div>
      )}

      {/* ===== تاب حذف الحرارة ===== */}
      {activeTab === 'temperature' && (
        <div className="tab-content">
          <div className="delete-modes">
            <label className="mode-radio">
              <input
                type="radio"
                value="single"
                checked={tempMode === 'single'}
                onChange={(e) => setTempMode(e.target.value)}
              />
              <span>حذف قراءة واحدة</span>
            </label>
            <label className="mode-radio">
              <input
                type="radio"
                value="all"
                checked={tempMode === 'all'}
                onChange={(e) => setTempMode(e.target.value)}
              />
              <span>حذف كل القراءات</span>
            </label>
          </div>

          <div className="form-group">
            <select
              value={selectedTempPourId}
              onChange={(e) => {
                setSelectedTempPourId(e.target.value);
                if (e.target.value) {
                  fetchTempReadings(e.target.value);
                }
              }}
              disabled={loadingPours}
            >
              <option value="">اختر صبة ({pours.length})</option>
              {pours.map((pour) => (
                <option key={pour._id} value={pour._id}>
                  {pour.station} - {new Date(pour.castingDate).toLocaleDateString('ar-EG')}
                </option>
              ))}
            </select>

            {tempMode === 'single' && (
              <select
                value={selectedReadingId}
                onChange={(e) => setSelectedReadingId(e.target.value)}
                disabled={loadingTempReadings || tempReadings.length === 0}
              >
                <option value="">اختر قراءة ({tempReadings.length})</option>
                {tempReadings.map((reading) => (
                  <option key={reading._id} value={reading._id}>
                    {new Date(reading.readingDate).toLocaleDateString('ar-EG')} - {reading.readingTime}
                  </option>
                ))}
              </select>
            )}
          </div>

          <button
            className="delete-btn"
            onClick={handleDeleteTempReading}
            disabled={loading}
          >
            {loading ? '⏳ جاري الحذف...' : '🗑️ حذف الآن'}
          </button>
        </div>
      )}

      {/* ===== تاب السجلات ===== */}
      {activeTab === 'logs' && (
        <div className="tab-content">
          <div className="logs-header">
            <h3>📊 سجل العمليات ({logs.length})</h3>
            <button className="clear-logs-btn" onClick={clearLogs}>
              🧹 مسح السجلات
            </button>
          </div>

          <div className="logs-list">
            {logs.length === 0 ? (
              <p className="no-logs">لا توجد عمليات حتى الآن</p>
            ) : (
              logs.map((log) => (
                <div key={log.id} className={`log-item ${log.status}`}>
                  <div className="log-time">{log.timestamp}</div>
                  <div className="log-action">{log.action}</div>
                  <div className="log-status">
                    {log.status === 'نجح' ? '✅' : '❌'} {log.status}
                  </div>
                  <div className="log-details">{log.details}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeleteDashboard;